import api from '@/shared/utils/apiConfig';
import { recoverIfMutationFinished } from '@/shared/utils/apiError';
import type { ApiResponse } from '@/shared/types/api';
import { isPayrollCalculationFinished } from '../utils/payrollCalculationFinished';
import { downloadBlob, filenameFromContentDisposition } from '@/shared/import-export/downloadCsv';
import { fetchPdfBlob } from '@/modules/print/api/pdf.api';
import type { PdfDisposition, PdfResponse } from '@/modules/print/types/print.types';
import type {
  AdjustPayrollEntryPayload,
  UpdatePayrollEntryLinePayload,
  CalculatePayrollPeriodPayload,
  CompensationAssignment,
  CompensationProfile,
  CreateCompensationVersionPayload,
  CreatePayrollPeriodPayload,
  CreatePayrollPaymentsPayload,
  CreatePayrollPaymentsResult,
  CreateSalaryComponentPayload,
  PayrollCommissionLine,
  PayrollEntryDetail,
  PayrollEntryListItem,
  PayrollKpiLine,
  PayrollPeriodDetail,
  PayrollReferralCommissionLine,
  PayrollPeriodListItem,
  PayrollPeriodListResult,
  PeriodExtraPayload,
  UpdatePeriodExtraPayload,
  OwnPayslipListItem,
  OwnPayslipListResult,
  PayrollAdvance,
  PayrollAdvanceListResult,
  PayrollAdvanceStaffRef,
  CreatePayrollAdvancePayload,
  RequestPayrollAdvancePayload,
  PayPayrollAdvancePayload,
  SalaryComponent,
  StaffCompensationResult,
  UpdatePayrollPeriodPayload,
  UpdateSalaryComponentPayload,
} from '../types/payroll';

export const payrollAPI = {
  listCompensationAssignments: async (): Promise<CompensationAssignment[]> => {
    const response = await api.get<ApiResponse<CompensationAssignment[]>>('/payroll/compensation');
    return response.data.data ?? [];
  },

  getStaffCompensation: async (staffId: string): Promise<StaffCompensationResult> => {
    const response = await api.get<ApiResponse<StaffCompensationResult>>(
      `/payroll/staff/${staffId}/compensation`
    );
    return response.data.data as StaffCompensationResult;
  },

  createStaffCompensation: async (
    staffId: string,
    payload: CreateCompensationVersionPayload
  ): Promise<CompensationProfile> => {
    const response = await api.post<ApiResponse<CompensationProfile>>(
      `/payroll/staff/${staffId}/compensation`,
      payload
    );
    return response.data.data as CompensationProfile;
  },

  updateStaffCompensation: async (
    staffId: string,
    profileId: string,
    payload: CreateCompensationVersionPayload
  ): Promise<CompensationProfile> => {
    const response = await api.patch<ApiResponse<CompensationProfile>>(
      `/payroll/staff/${staffId}/compensation/${profileId}`,
      payload
    );
    return response.data.data as CompensationProfile;
  },

  listPeriods: async (params: {
    page?: number;
    limit?: number;
    status?: string;
    periodKey?: string;
    clinicId?: string;
    sort?: string;
  }): Promise<PayrollPeriodListResult> => {
    const response = await api.get<ApiResponse<PayrollPeriodListItem[]>>('/payroll/periods', {
      params,
    });
    return {
      data: response.data.data ?? [],
      total: response.data.total ?? 0,
      page: response.data.page ?? 1,
      pages: response.data.pages ?? 1,
    };
  },

  createPeriod: async (payload: CreatePayrollPeriodPayload): Promise<PayrollPeriodDetail> => {
    const response = await api.post<ApiResponse<PayrollPeriodDetail>>('/payroll/periods', payload);
    return response.data.data as PayrollPeriodDetail;
  },

  getPeriod: async (id: string, options?: { timeout?: number }): Promise<PayrollPeriodDetail> => {
    const response = await api.get<ApiResponse<PayrollPeriodDetail>>(`/payroll/periods/${id}`, {
      timeout: options?.timeout,
    });
    return response.data.data as PayrollPeriodDetail;
  },

  updatePeriod: async (
    id: string,
    payload: UpdatePayrollPeriodPayload
  ): Promise<PayrollPeriodDetail> => {
    const response = await api.patch<ApiResponse<PayrollPeriodDetail>>(
      `/payroll/periods/${id}`,
      payload
    );
    return response.data.data as PayrollPeriodDetail;
  },

  calculatePeriod: async (
    id: string,
    payload?: CalculatePayrollPeriodPayload,
    options?: { calculatedAtBefore?: string | null }
  ): Promise<PayrollPeriodDetail> => {
    const calculatedAtBefore = options?.calculatedAtBefore ?? null;
    try {
      const response = await api.post<ApiResponse<PayrollPeriodDetail>>(
        `/payroll/periods/${id}/calculate`,
        payload ?? {}
      );
      return response.data.data as PayrollPeriodDetail;
    } catch (err) {
      return recoverIfMutationFinished(
        err,
        () => payrollAPI.getPeriod(id, { timeout: 30_000 }),
        (period) => isPayrollCalculationFinished(period, calculatedAtBefore)
      );
    }
  },

  addPeriodExtra: async (
    periodId: string,
    payload: PeriodExtraPayload
  ): Promise<PayrollPeriodDetail> => {
    const response = await api.post<ApiResponse<PayrollPeriodDetail>>(
      `/payroll/periods/${periodId}/extras`,
      payload
    );
    return response.data.data as PayrollPeriodDetail;
  },

  updatePeriodExtra: async (
    periodId: string,
    extraId: string,
    payload: UpdatePeriodExtraPayload
  ): Promise<PayrollPeriodDetail> => {
    const response = await api.patch<ApiResponse<PayrollPeriodDetail>>(
      `/payroll/periods/${periodId}/extras/${extraId}`,
      payload
    );
    return response.data.data as PayrollPeriodDetail;
  },

  deletePeriodExtra: async (periodId: string, extraId: string): Promise<PayrollPeriodDetail> => {
    const response = await api.delete<ApiResponse<PayrollPeriodDetail>>(
      `/payroll/periods/${periodId}/extras/${extraId}`
    );
    return response.data.data as PayrollPeriodDetail;
  },

  approvePeriod: async (id: string): Promise<PayrollPeriodDetail> => {
    const response = await api.post<ApiResponse<PayrollPeriodDetail>>(
      `/payroll/periods/${id}/approve`
    );
    return response.data.data as PayrollPeriodDetail;
  },

  rejectPeriod: async (id: string): Promise<PayrollPeriodDetail> => {
    const response = await api.post<ApiResponse<PayrollPeriodDetail>>(
      `/payroll/periods/${id}/reject`
    );
    return response.data.data as PayrollPeriodDetail;
  },

  lockPeriod: async (id: string): Promise<PayrollPeriodDetail> => {
    const response = await api.post<ApiResponse<PayrollPeriodDetail>>(`/payroll/periods/${id}/lock`);
    return response.data.data as PayrollPeriodDetail;
  },

  unlockPeriod: async (id: string, reason: string): Promise<PayrollPeriodDetail> => {
    const response = await api.post<ApiResponse<PayrollPeriodDetail>>(
      `/payroll/periods/${id}/unlock`,
      { reason }
    );
    return response.data.data as PayrollPeriodDetail;
  },

  cancelPeriod: async (id: string): Promise<PayrollPeriodDetail> => {
    const response = await api.post<ApiResponse<PayrollPeriodDetail>>(
      `/payroll/periods/${id}/cancel`
    );
    return response.data.data as PayrollPeriodDetail;
  },

  createPayments: async (
    id: string,
    payload: CreatePayrollPaymentsPayload
  ): Promise<CreatePayrollPaymentsResult> => {
    const response = await api.post<ApiResponse<CreatePayrollPaymentsResult>>(
      `/payroll/periods/${id}/payments`,
      payload
    );
    return response.data.data as CreatePayrollPaymentsResult;
  },

  listEntries: async (periodId: string): Promise<PayrollEntryListItem[]> => {
    const response = await api.get<ApiResponse<PayrollEntryListItem[]>>(
      `/payroll/periods/${periodId}/entries`
    );
    return response.data.data ?? [];
  },

  getEntry: async (id: string): Promise<PayrollEntryDetail> => {
    const response = await api.get<ApiResponse<PayrollEntryDetail>>(`/payroll/entries/${id}`);
    return response.data.data as PayrollEntryDetail;
  },

  listEntryKpiLines: async (id: string): Promise<PayrollKpiLine[]> => {
    const response = await api.get<ApiResponse<PayrollKpiLine[]>>(
      `/payroll/entries/${id}/kpi-lines`
    );
    return response.data.data ?? [];
  },

  listEntryCommissionLines: async (id: string): Promise<PayrollCommissionLine[]> => {
    const response = await api.get<ApiResponse<PayrollCommissionLine[]>>(
      `/payroll/entries/${id}/commission-lines`
    );
    return response.data.data ?? [];
  },

  listEntryReferralLines: async (id: string): Promise<PayrollReferralCommissionLine[]> => {
    const response = await api.get<ApiResponse<PayrollReferralCommissionLine[]>>(
      `/payroll/entries/${id}/referral-commission-lines`
    );
    return response.data.data ?? [];
  },

  disputeEntry: async (id: string): Promise<PayrollEntryDetail> => {
    const response = await api.post<ApiResponse<PayrollEntryDetail>>(
      `/payroll/entries/${id}/dispute`
    );
    return response.data.data as PayrollEntryDetail;
  },

  confirmOwnPayslip: async (id: string): Promise<PayrollEntryDetail> => {
    const response = await api.post<ApiResponse<PayrollEntryDetail>>(
      `/payroll/me/payslips/${id}/confirm`
    );
    return response.data.data as PayrollEntryDetail;
  },

  disputeOwnPayslip: async (id: string): Promise<PayrollEntryDetail> => {
    const response = await api.post<ApiResponse<PayrollEntryDetail>>(
      `/payroll/me/payslips/${id}/dispute`
    );
    return response.data.data as PayrollEntryDetail;
  },

  adjustEntry: async (
    id: string,
    payload: AdjustPayrollEntryPayload
  ): Promise<PayrollEntryDetail> => {
    const response = await api.post<ApiResponse<PayrollEntryDetail>>(
      `/payroll/entries/${id}/adjustments`,
      payload
    );
    return response.data.data as PayrollEntryDetail;
  },

  updateEntryLine: async (
    id: string,
    lineIndex: number,
    payload: UpdatePayrollEntryLinePayload
  ): Promise<PayrollEntryDetail> => {
    const response = await api.patch<ApiResponse<PayrollEntryDetail>>(
      `/payroll/entries/${id}/lines/${lineIndex}`,
      payload
    );
    return response.data.data as PayrollEntryDetail;
  },

  exportPeriod: async (id: string): Promise<void> => {
    try {
      const response = await api.get(`/payroll/periods/${id}/export`, {
        responseType: 'blob',
      });
      const contentType = response.headers['content-type'] as string | undefined;
      if (contentType?.includes('application/json')) {
        const parsed = JSON.parse(await (response.data as Blob).text()) as {
          message?: string;
          code?: string;
        };
        const err = new Error(parsed.message || 'Export failed') as Error & { code?: string };
        err.code = parsed.code;
        throw err;
      }
      const filename = filenameFromContentDisposition(
        response.headers['content-disposition'] as string | undefined,
        `bang-luong.xlsx`
      );
      downloadBlob(response.data as Blob, filename);
    } catch (error) {
      const data = (error as { response?: { data?: Blob } }).response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text()) as { message?: string; code?: string };
          const err = new Error(parsed.message || 'Export failed') as Error & { code?: string };
          err.code = parsed.code;
          throw err;
        } catch (inner) {
          if (inner instanceof Error && (inner as { code?: string }).code) throw inner;
        }
      }
      throw error;
    }
  },

  getPayslipPdf: async (
    id: string,
    disposition: PdfDisposition = 'inline'
  ): Promise<PdfResponse> =>
    fetchPdfBlob(`/payroll/entries/${id}/payslip`, disposition, `phieu-luong.pdf`),

  listOwnPayslips: async (params: {
    page?: number;
    limit?: number;
  }): Promise<OwnPayslipListResult> => {
    const response = await api.get<ApiResponse<OwnPayslipListItem[]>>('/payroll/me/payslips', {
      params,
    });
    return {
      data: response.data.data ?? [],
      total: response.data.total ?? 0,
      page: response.data.page ?? 1,
      pages: response.data.pages ?? 1,
    };
  },

  listStaffPayslips: async (
    staffId: string,
    params: { page?: number; limit?: number }
  ): Promise<OwnPayslipListResult> => {
    const response = await api.get<ApiResponse<OwnPayslipListItem[]>>(
      `/payroll/staff/${staffId}/payslips`,
      { params }
    );
    return {
      data: response.data.data ?? [],
      total: response.data.total ?? 0,
      page: response.data.page ?? 1,
      pages: response.data.pages ?? 1,
    };
  },

  getOwnPayslipPdf: async (
    entryId: string,
    disposition: PdfDisposition = 'inline'
  ): Promise<PdfResponse> =>
    fetchPdfBlob(`/payroll/me/payslips/${entryId}/pdf`, disposition, `phieu-luong.pdf`),

  listAdvances: async (params: {
    page?: number;
    limit?: number;
    staff?: string;
    status?: string;
    clinicId?: string;
    sort?: string;
  }): Promise<PayrollAdvanceListResult> => {
    const response = await api.get<ApiResponse<PayrollAdvance[]>>('/payroll/advances', {
      params,
    });
    return {
      data: response.data.data ?? [],
      total: response.data.total ?? 0,
      page: response.data.page ?? 1,
      pages: response.data.pages ?? 1,
    };
  },

  createAdvance: async (payload: CreatePayrollAdvancePayload): Promise<PayrollAdvance> => {
    const response = await api.post<ApiResponse<PayrollAdvance>>('/payroll/advances', payload);
    return response.data.data as PayrollAdvance;
  },

  listMyAdvances: async (params: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PayrollAdvanceListResult> => {
    const response = await api.get<ApiResponse<PayrollAdvance[]>>('/payroll/me/advances', {
      params,
    });
    return {
      data: response.data.data ?? [],
      total: response.data.total ?? 0,
      page: response.data.page ?? 1,
      pages: response.data.pages ?? 1,
    };
  },

  getAdvance: async (id: string): Promise<PayrollAdvance> => {
    const response = await api.get<ApiResponse<PayrollAdvance>>(`/payroll/advances/${id}`);
    return response.data.data as PayrollAdvance;
  },

  requestAdvance: async (payload: RequestPayrollAdvancePayload): Promise<PayrollAdvance> => {
    const response = await api.post<ApiResponse<PayrollAdvance>>('/payroll/me/advances', payload);
    return response.data.data as PayrollAdvance;
  },

  updateMyAdvance: async (
    id: string,
    payload: RequestPayrollAdvancePayload
  ): Promise<PayrollAdvance> => {
    const response = await api.patch<ApiResponse<PayrollAdvance>>(
      `/payroll/me/advances/${id}`,
      payload
    );
    return response.data.data as PayrollAdvance;
  },

  cancelMyAdvance: async (id: string): Promise<PayrollAdvance> => {
    const response = await api.post<ApiResponse<PayrollAdvance>>(`/payroll/me/advances/${id}/cancel`);
    return response.data.data as PayrollAdvance;
  },

  approveAdvance: async (id: string): Promise<PayrollAdvance> => {
    const response = await api.post<ApiResponse<PayrollAdvance>>(`/payroll/advances/${id}/approve`);
    return response.data.data as PayrollAdvance;
  },

  rejectAdvance: async (id: string, rejectReason: string): Promise<PayrollAdvance> => {
    const response = await api.post<ApiResponse<PayrollAdvance>>(`/payroll/advances/${id}/reject`, {
      rejectReason,
    });
    return response.data.data as PayrollAdvance;
  },

  cancelAdvance: async (id: string): Promise<PayrollAdvance> => {
    const response = await api.post<ApiResponse<PayrollAdvance>>(`/payroll/advances/${id}/cancel`);
    return response.data.data as PayrollAdvance;
  },

  payAdvance: async (id: string, payload: PayPayrollAdvancePayload): Promise<PayrollAdvance> => {
    const response = await api.post<ApiResponse<PayrollAdvance>>(
      `/payroll/advances/${id}/pay`,
      payload
    );
    return response.data.data as PayrollAdvance;
  },

  listAdvanceStaffRefs: async (): Promise<PayrollAdvanceStaffRef[]> => {
    const response = await api.get<ApiResponse<PayrollAdvanceStaffRef[]>>(
      '/payroll/advances/staff-refs'
    );
    return response.data.data ?? [];
  },

  listSalaryComponents: async (
    includeInactive = false,
    sort?: string
  ): Promise<SalaryComponent[]> => {
    const response = await api.get<ApiResponse<SalaryComponent[]>>('/payroll/components', {
      params: { includeInactive: includeInactive ? 'true' : undefined, sort },
    });
    return response.data.data ?? [];
  },

  createSalaryComponent: async (payload: CreateSalaryComponentPayload): Promise<SalaryComponent> => {
    const response = await api.post<ApiResponse<SalaryComponent>>('/payroll/components', payload);
    return response.data.data as SalaryComponent;
  },

  updateSalaryComponent: async (
    id: string,
    payload: UpdateSalaryComponentPayload
  ): Promise<SalaryComponent> => {
    const response = await api.patch<ApiResponse<SalaryComponent>>(
      `/payroll/components/${id}`,
      payload
    );
    return response.data.data as SalaryComponent;
  },

};
